// Pit-Stop-Umweg + Wiedereinstieg
// ===============================
//
// Für einen POI voraus: von der aktuellen Position auf der Rennstrecke abbiegen,
// zum POI fahren und wieder auf die Strecke einsteigen — und zwar VORWÄRTS (in
// Fahrtrichtung), nicht rückwärts. "Vorwärts" = Routenpunkt mit Index >= dem
// aktuellen Fortschritt (die GPX ist Start→Ziel geordnet, der Fahrer bewegt
// sich mit steigendem Index).
//
// Penalty = (Straße Position→POI + Straße POI→Wiedereinstieg) − die Strecke, die
// man dadurch auf der Rennroute überspringt. So sieht man den echten Zeitverlust.

import { haversine, DEFAULT_REGION, type RegionId } from '@adtu/shared';
import { postDetour } from './naviApi';
import type { RoutePoint } from './routeBus';

export interface DetourResult {
  /** Netto-Mehraufwand (km) gegenüber der Originalstrecke, oder null. */
  detourKm: number | null;
  /** Netto-Mehr-Höhenmeter (m), oder null. */
  detourHm: number | null;
  /** [lon,lat] optimaler Ausstieg auf der Rennstrecke. */
  exit: [number, number] | null;
  /** [lon,lat] optimaler Wiedereinstieg auf der Rennstrecke. */
  rejoin: [number, number] | null;
}

/** Index des nächstgelegenen Routenpunkts zu (lat,lon). */
export function nearestRouteIndex(route: RoutePoint[], lat: number, lon: number): number {
  let bestIdx = 0;
  let bestD = Infinity;
  for (let i = 0; i < route.length; i++) {
    const p = route[i];
    if (!p) continue;
    const d = haversine(lat, lon, p[0], p[1]);
    if (d < bestD) {
      bestD = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Wiedereinstiegs-Index: der Routenpunkt, der dem POI am nächsten ist, aber
 * NICHT hinter dem aktuellen Fortschritt (fromIdx) liegt → kein Zurückfahren.
 */
export function forwardRejoinIndex(
  route: RoutePoint[],
  fromIdx: number,
  poiLat: number,
  poiLon: number,
): number {
  let bestIdx = fromIdx;
  let bestD = Infinity;
  for (let i = fromIdx; i < route.length; i++) {
    const p = route[i];
    if (!p) continue;
    const d = haversine(poiLat, poiLon, p[0], p[1]);
    if (d < bestD) {
      bestD = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/** Strecke entlang der Route zwischen zwei Indizes (Meter). */
function routeDistBetween(route: RoutePoint[], i: number, j: number): number {
  const lo = Math.min(i, j);
  const hi = Math.max(i, j);
  let m = 0;
  for (let k = lo; k < hi; k++) {
    const a = route[k];
    const b = route[k + 1];
    if (a && b) m += haversine(a[0], a[1], b[0], b[1]);
  }
  return m;
}

/** Anstieg (Höhenmeter) entlang der Originalstrecke zwischen zwei Indizes —
 *  aus den GPX-Höhen (route[k][2]). Punkte ohne Höhe werden übersprungen. */
function routeAscentBetween(route: RoutePoint[], i: number, j: number): number {
  const lo = Math.min(i, j);
  const hi = Math.max(i, j);
  let asc = 0;
  let prev = NaN;
  for (let k = lo; k <= hi; k++) {
    const e = route[k]?.[2];
    if (typeof e === 'number' && Number.isFinite(e)) {
      if (Number.isFinite(prev) && e > prev) asc += e - prev;
      prev = e;
    }
  }
  return asc;
}

/** [lon,lat] des nächstgelegenen Routenpunkts zu (lat,lon) — für den einfachen
 *  Weg ohne Fortschrittsbezug. */
export function nearestAnchor(
  route: RoutePoint[],
  lat: number,
  lon: number,
): [number, number] | null {
  if (!route.length) return null;
  const i = nearestRouteIndex(route, lat, lon);
  const p = route[i];
  return p ? [p[1], p[0]] : null;
}

/**
 * Vorwärts-Wiedereinstieg als [lon,lat] für die Navigation: nächster
 * Routenpunkt zum POI ab dem aktuellen Fortschritt.
 */
export function forwardRejoin(
  route: RoutePoint[],
  fromLat: number,
  fromLon: number,
  poiLat: number,
  poiLon: number,
): [number, number] | null {
  if (route.length < 2) return null;
  const fromIdx = nearestRouteIndex(route, fromLat, fromLon);
  const rejIdx = forwardRejoinIndex(route, fromIdx, poiLat, poiLon);
  const p = route[rejIdx];
  return p ? [p[1], p[0]] : null;
}

/**
 * Mathematisch optimaler Umweg-Penalty: das Backend rechnet per one-to-all-
 * Dijkstra das beste (Ausstieg, Wiedereinstieg)-Paar und liefert den Netto-
 * Mehraufwand (+km/+hm). Wir schicken nur ein Vorwärts-Fenster der Rennstrecke
 * ab dem aktuellen Fortschritt (deckt alle sinnvollen Ein-/Ausstiege ab).
 */
export async function computeDetour(
  route: RoutePoint[],
  originLat: number,
  originLon: number,
  poiLat: number,
  poiLon: number,
  region: RegionId = DEFAULT_REGION,
): Promise<DetourResult> {
  const none: DetourResult = { detourKm: null, detourHm: null, exit: null, rejoin: null };
  if (route.length < 2) return none;
  const fromIdx = nearestRouteIndex(route, originLat, originLon);
  // Vorwärts-Fenster als [lon,lat,ele], ausgedünnt auf ~150 m Abstand (deutlich
  // weniger Backend-Snaps = schneller), begrenzt auf ~45 km / 300 Punkte.
  const window: Array<[number, number, number?]> = [];
  let acc = 0;
  let prev: RoutePoint | null = null;
  let lastIncl: RoutePoint | null = null;
  for (let i = fromIdx; i < route.length && window.length < 300; i++) {
    const p = route[i];
    if (!p) continue;
    if (prev) {
      acc += haversine(prev[0], prev[1], p[0], p[1]);
      if (acc > 45000) break;
    }
    if (!lastIncl || haversine(lastIncl[0], lastIncl[1], p[0], p[1]) >= 150) {
      window.push([p[1], p[0], p[2]]);
      lastIncl = p;
    }
    prev = p;
  }
  if (window.length < 2) return none;
  try {
    const res = await postDetour([poiLon, poiLat], window, region);
    if (!res.ok || res.extraKm == null) return none;
    return {
      detourKm: res.extraKm,
      detourHm: res.extraHm ?? 0,
      exit: res.exit ?? null,
      rejoin: res.rejoin ?? null,
    };
  } catch {
    return none;
  }
}
